"use client";

import { useRef, useState } from "react";
import { uploadFileDirect, CreatorVoiceError } from "@/lib/creatorVoice/api";
import { useJob } from "@/lib/creatorVoice/useJob";
import { ErrorBox, JobProgress, AudioResult } from "@/components/creatorVoice/CreatorVoiceUI";
import Spinner from "@/components/Spinner";

export default function AudioCleanupTool() {
  const [file, setFile] = useState<File | null>(null);
  const [removeNoise, setRemoveNoise] = useState(true);
  const [removeSilence, setRemoveSilence] = useState(true);
  const [removeFillers, setRemoveFillers] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { job, submitting, error, run } = useJob();

  async function handleSubmit() {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const audioUrl = await uploadFileDirect(file);
      await run("audio_cleanup", {
        audio_url: audioUrl,
        remove_noise: removeNoise,
        remove_silence: removeSilence,
        remove_filler_words: removeFillers,
      });
    } catch (e) {
      setUploadError(e instanceof CreatorVoiceError ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Upload a WAV audio file</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="mt-2 flex h-24 cursor-pointer items-center justify-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
      >
        {file ? file.name : "Click to choose a .wav file"}
        <input
          ref={inputRef}
          type="file"
          accept="audio/wav,audio/x-wav"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={removeNoise} onChange={(e) => setRemoveNoise(e.target.checked)} /> Remove noise
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={removeSilence} onChange={(e) => setRemoveSilence(e.target.checked)} /> Remove silence
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={removeFillers} onChange={(e) => setRemoveFillers(e.target.checked)} /> Remove filler words
        </label>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={uploading || submitting || !file}
          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
        >
          {uploading || submitting ? <Spinner className="h-4 w-4" /> : null}
          {uploading ? "Uploading…" : submitting ? "Submitting…" : "Clean Audio"}
        </button>
      </div>

      {uploadError ? <ErrorBox message={uploadError} /> : null}
      {error ? <ErrorBox message={error} /> : null}
      <JobProgress job={job} />
      <AudioResult job={job} />
    </div>
  );
}
